import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  Card,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Button,
  Alert,
  Table,
  Chip,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Modal,
  ModalDialog,
  ModalClose,
  Badge
} from '@mui/joy';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import axiosInstance from '../util/axiosConfig';
import { useSetPageHeaderAction } from '../context/PageHeaderActionsContext';
import ResponsiveDetailTable from '../components/ResponsiveDetailTable';
import DetailFieldCard from '../components/DetailFieldCard';
import { BRAND } from '../brand';

const GROUP_LABELS = { day: 'Daily', week: 'Weekly', month: 'Monthly' };

const COLORS = [BRAND.colors.navy, '#2f77d0', '#2e7d32', '#6a1b9a', '#d32f2f', '#00838f'];

const todayStr = () => new Date().toISOString().slice(0, 10);
const startOfYearStr = () => `${new Date().getFullYear()}-01-01`;

const fmtMoney = (n) => {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const STATUS_LABELS = {
  purchased: 'Purchased',
  installed: 'Installed',
  in_service: 'In Service',
  broken: 'Broken',
  scraped: 'Scraped'
};

const daysToWarranty = (warrantyUntil) => {
  if (!warrantyUntil) return null;
  const today = new Date(`${todayStr()}T00:00:00`);
  const w = new Date(`${String(warrantyUntil).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(w.getTime())) return null;
  return Math.round((w - today) / (24 * 60 * 60 * 1000));
};

const EquipmentReports = () => {
  const [from, setFrom] = useState(startOfYearStr());
  const [to, setTo] = useState(todayStr());
  const [group, setGroup] = useState('month');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [historyTab, setHistoryTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(startOfYearStr());
  const [draftTo, setDraftTo] = useState(todayStr());
  const [draftGroup, setDraftGroup] = useState('month');
  const [draftSearch, setDraftSearch] = useState('');
  const [draftStatusFilter, setDraftStatusFilter] = useState('');

  const runReport = async (overrides = {}) => {
    const reportFrom = overrides.from ?? from;
    const reportTo = overrides.to ?? to;
    const reportGroup = overrides.group ?? group;
    const reportSearch = overrides.search ?? search;
    const reportStatus = overrides.statusFilter ?? statusFilter;

    try {
      setLoading(true);
      setError('');
      const params = { from: reportFrom, to: reportTo, group: reportGroup };
      if (reportSearch.trim()) params.search = reportSearch.trim();
      if (reportStatus) params.status = reportStatus;
      const res = await axiosInstance.get('/reports/equipment', { params });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load equipment report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openFilter = useCallback(() => {
    setDraftFrom(from);
    setDraftTo(to);
    setDraftGroup(group);
    setDraftSearch(search);
    setDraftStatusFilter(statusFilter);
    setFilterOpen(true);
  }, [from, to, group, search, statusFilter]);

  const applyFilters = async () => {
    setFrom(draftFrom);
    setTo(draftTo);
    setGroup(draftGroup);
    setSearch(draftSearch);
    setStatusFilter(draftStatusFilter);
    setFilterOpen(false);
    await runReport({
      from: draftFrom,
      to: draftTo,
      group: draftGroup,
      search: draftSearch,
      statusFilter: draftStatusFilter
    });
  };

  const hasActiveFilters =
    from !== startOfYearStr() ||
    group !== 'month' ||
    Boolean(search.trim()) ||
    Boolean(statusFilter);

  const headerAction = useMemo(
    () => (
      <Badge invisible={!hasActiveFilters} color="primary" variant="solid" size="sm">
        <Button
          variant="outlined"
          color="primary"
          size="sm"
          startDecorator={<FilterListIcon />}
          onClick={openFilter}
        >
          Filter
        </Button>
      </Badge>
    ),
    [hasActiveFilters, openFilter]
  );

  useSetPageHeaderAction(headerAction);

  const pieData = useMemo(() => {
    const s = data?.summary;
    if (!s) return [];
    return [
      { name: 'Purchased', value: Number(s.purchased || 0) },
      { name: 'Installed', value: Number(s.installed || 0) },
      { name: 'In Service', value: Number(s.in_service || 0) },
      { name: 'Broken', value: Number(s.broken || 0) },
      { name: 'Scraped', value: Number(s.scraped || 0) }
    ].filter((d) => d.value > 0);
  }, [data]);

  const needsAttention = (item) => {
    if (item.status === 'broken' || item.status === 'in_service') return true;
    const days = daysToWarranty(item.warranty_until);
    return days != null && days >= 0 && days <= 30;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Chip size="sm" variant="soft">
          {from} → {to}
        </Chip>
        <Chip size="sm" variant="soft">
          {GROUP_LABELS[group] || group}
        </Chip>
        {statusFilter ? (
          <Chip size="sm" variant="soft" color="primary">
            {STATUS_LABELS[statusFilter] || statusFilter}
          </Chip>
        ) : (
          <Chip size="sm" variant="soft">
            All statuses
          </Chip>
        )}
        {search.trim() ? (
          <Chip size="sm" variant="soft" color="primary">
            Search: {search.trim()}
          </Chip>
        ) : null}
      </Box>

      {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

      <Modal open={filterOpen} onClose={() => setFilterOpen(false)}>
        <ModalDialog sx={{ width: { xs: '95vw', sm: 480 }, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h3" sx={{ color: 'primary.500', mb: 2 }}>
            Report filters
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl>
              <FormLabel>From</FormLabel>
              <Input type="date" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>To</FormLabel>
              <Input type="date" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>Group</FormLabel>
              <Select value={draftGroup} onChange={(_, v) => setDraftGroup(v || 'month')}>
                <Option value="day">Daily</Option>
                <Option value="week">Weekly</Option>
                <Option value="month">Monthly</Option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Status</FormLabel>
              <Select value={draftStatusFilter} onChange={(_, v) => setDraftStatusFilter(v || '')} placeholder="All">
                <Option value="">All</Option>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <Option key={k} value={k}>{v}</Option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Search</FormLabel>
              <Input
                placeholder="Name, serial..."
                value={draftSearch}
                onChange={(e) => setDraftSearch(e.target.value)}
              />
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 1 }}>
              <Button variant="plain" color="neutral" onClick={() => setFilterOpen(false)}>
                Cancel
              </Button>
              <Button
                loading={loading}
                onClick={applyFilters}
                sx={{ backgroundColor: 'primary.500', '&:hover': { backgroundColor: 'primary.600' } }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      {data?.summary && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
          <Chip color="primary" variant="soft">Total: {data.summary.total}</Chip>
          <Chip variant="soft">Installed: {data.summary.installed}</Chip>
          <Chip color="warning" variant="soft">In Service: {data.summary.in_service}</Chip>
          <Chip color="danger" variant="soft">Broken: {data.summary.broken}</Chip>
          <Chip variant="soft">Warranty: {data.summary.under_warranty}</Chip>
          <Chip color="success" variant="soft">AMC Active: {data.summary.amc_active}</Chip>
          <Chip variant="soft">Uncovered: {data.summary.uncovered}</Chip>
          <Chip color="warning" variant="outlined">Open Service: {data.summary.open_service}</Chip>
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
        <Card sx={{ p: 2 }}>
          <Typography level="h3" sx={{ mb: 1, color: 'primary.500' }}>Status Distribution</Typography>
          <Box sx={{ height: 280 }}>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography level="body-sm" sx={{ opacity: 0.7, mt: 4, textAlign: 'center' }}>No equipment data</Typography>
            )}
          </Box>
        </Card>

        <Card sx={{ p: 2 }}>
          <Typography level="h3" sx={{ mb: 1, color: 'primary.500' }}>Lifecycle Changes in Range</Typography>
          <Box sx={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.series || []} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="installed" fill={COLORS[2]} name="Installed" />
                <Bar dataKey="in_service" fill={COLORS[4]} name="In Service" />
                <Bar dataKey="broken" fill={COLORS[5]} name="Broken" />
                <Bar dataKey="scraped" fill={COLORS[3]} name="Scraped" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Card>
      </Box>

      <Card sx={{ p: 2, mb: 3 }}>
        <Typography level="h3" sx={{ mb: 2, color: 'primary.500' }}>Current Assets</Typography>
        <ResponsiveDetailTable
          mobile={
            (data?.items || []).length === 0 ? (
              <Typography level="body-sm" sx={{ opacity: 0.7, textAlign: 'center', py: 2 }}>
                No equipment found
              </Typography>
            ) : (
              (data?.items || []).map((item) => (
                <DetailFieldCard
                  key={item.id}
                  highlight={needsAttention(item)}
                  title={item.equipment_name}
                  subtitle={item.brand_make || undefined}
                  fields={[
                    { label: 'Serial', value: item.serial_no || '—' },
                    {
                      label: 'Status',
                      value: (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Chip size="sm" variant="soft">{STATUS_LABELS[item.status] || item.status}</Chip>
                          {item.has_open_service && <Chip size="sm" color="warning">Service</Chip>}
                        </Box>
                      )
                    },
                    {
                      label: 'Coverage',
                      value: (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                          {item.under_warranty && <Chip size="sm" color="primary" variant="soft">Warranty</Chip>}
                          {item.amc_active && <Chip size="sm" color="success" variant="soft">AMC</Chip>}
                          {item.uncovered && <Chip size="sm" variant="soft">Uncovered</Chip>}
                          {!item.under_warranty && !item.amc_active && !item.uncovered && '—'}
                        </Box>
                      )
                    },
                    {
                      label: 'Warranty',
                      value: item.warranty_until
                        ? new Date(item.warranty_until).toLocaleDateString()
                        : '—'
                    },
                    { label: 'Vendor', value: item.vendor_name || '—' },
                    { label: 'Location', value: item.location || '—' }
                  ]}
                />
              ))
            )
          }
        >
          <Table size="sm" stripe="odd" hoverRow sx={{ '& thead th': { backgroundColor: 'primary.500', color: 'white' } }}>
            <thead>
              <tr>
                <th>Equipment</th>
                <th>Serial</th>
                <th>Status</th>
                <th>Coverage</th>
                <th>Warranty</th>
                <th>Vendor</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((item) => (
                <tr
                  key={item.id}
                  style={needsAttention(item) ? { backgroundColor: '#fff8e1' } : undefined}
                >
                  <td>
                    <Typography fontWeight={600} sx={{ color: BRAND.colors.navy }}>{item.equipment_name}</Typography>
                    {item.brand_make && <Typography level="body-xs">{item.brand_make}</Typography>}
                  </td>
                  <td>{item.serial_no || '—'}</td>
                  <td>
                    <Chip size="sm" variant="soft">{STATUS_LABELS[item.status] || item.status}</Chip>
                    {item.has_open_service && <Chip size="sm" color="warning" sx={{ ml: 0.5 }}>Service</Chip>}
                  </td>
                  <td>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {item.under_warranty && <Chip size="sm" color="primary" variant="soft">Warranty</Chip>}
                      {item.amc_active && <Chip size="sm" color="success" variant="soft">AMC</Chip>}
                      {item.uncovered && <Chip size="sm" variant="soft">Uncovered</Chip>}
                    </Box>
                  </td>
                  <td>
                    {item.warranty_until
                      ? new Date(item.warranty_until).toLocaleDateString()
                      : '—'}
                  </td>
                  <td>{item.vendor_name || '—'}</td>
                  <td>{item.location || '—'}</td>
                </tr>
              ))}
              {(data?.items || []).length === 0 && (
                <tr><td colSpan={7} style={{ opacity: 0.7 }}>No equipment found</td></tr>
              )}
            </tbody>
          </Table>
        </ResponsiveDetailTable>
      </Card>

      <Card sx={{ p: 2 }}>
        <Typography level="h3" sx={{ mb: 2, color: 'primary.500' }}>History ({from} to {to})</Typography>
        <Tabs value={historyTab} onChange={(_, v) => setHistoryTab(v)}>
          <TabList>
            <Tab>Lifecycle</Tab>
            <Tab>Service</Tab>
            <Tab>AMC</Tab>
          </TabList>

          <TabPanel value={0} sx={{ px: 0 }}>
            <ResponsiveDetailTable
              mobile={
                (data?.statusHistory || []).length === 0 ? (
                  <Typography level="body-sm" sx={{ opacity: 0.7, textAlign: 'center', py: 2 }}>
                    No lifecycle history in range
                  </Typography>
                ) : (
                  (data?.statusHistory || []).map((h) => (
                    <DetailFieldCard
                      key={h.id}
                      title={h.equipment_name}
                      subtitle={h.change_date ? new Date(h.change_date).toLocaleString() : '—'}
                      fields={[
                        { label: 'From', value: h.previous_status ? STATUS_LABELS[h.previous_status] : '—' },
                        { label: 'To', value: STATUS_LABELS[h.new_status] || h.new_status },
                        { label: 'Remarks', value: h.remarks || '—' },
                        { label: 'By', value: h.created_by_name || '—' }
                      ]}
                    />
                  ))
                )
              }
            >
              <Table size="sm" stripe="odd" sx={{ '& thead th': { backgroundColor: 'primary.500', color: 'white' } }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Equipment</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Remarks</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.statusHistory || []).map((h) => (
                    <tr key={h.id}>
                      <td>{h.change_date ? new Date(h.change_date).toLocaleString() : '—'}</td>
                      <td>{h.equipment_name}</td>
                      <td>{h.previous_status ? STATUS_LABELS[h.previous_status] : '—'}</td>
                      <td>{STATUS_LABELS[h.new_status] || h.new_status}</td>
                      <td>{h.remarks || '—'}</td>
                      <td>{h.created_by_name || '—'}</td>
                    </tr>
                  ))}
                  {(data?.statusHistory || []).length === 0 && (
                    <tr><td colSpan={6} style={{ opacity: 0.7 }}>No lifecycle history in range</td></tr>
                  )}
                </tbody>
              </Table>
            </ResponsiveDetailTable>
          </TabPanel>

          <TabPanel value={1} sx={{ px: 0 }}>
            <ResponsiveDetailTable
              mobile={
                (data?.serviceHistory || []).length === 0 ? (
                  <Typography level="body-sm" sx={{ opacity: 0.7, textAlign: 'center', py: 2 }}>
                    No service records in range
                  </Typography>
                ) : (
                  (data?.serviceHistory || []).map((s) => (
                    <DetailFieldCard
                      key={s.id}
                      title={s.equipment_name}
                      subtitle={s.problem_reported}
                      fields={[
                        { label: 'Provider', value: s.service_provider || '—' },
                        { label: 'Technician', value: s.technician_name || '—' },
                        {
                          label: 'Out / In',
                          value: `${s.out_date ? new Date(s.out_date).toLocaleDateString() : '—'} / ${s.in_date ? new Date(s.in_date).toLocaleDateString() : '—'}`
                        },
                        { label: 'Charge', value: s.service_charge != null ? `₹${fmtMoney(s.service_charge)}` : '—' },
                        { label: 'Status', value: s.status }
                      ]}
                    />
                  ))
                )
              }
            >
              <Table size="sm" stripe="odd" sx={{ '& thead th': { backgroundColor: 'primary.500', color: 'white' } }}>
                <thead>
                  <tr>
                    <th>Equipment</th>
                    <th>Problem</th>
                    <th>Provider</th>
                    <th>Technician</th>
                    <th>Out / In</th>
                    <th>Charge</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.serviceHistory || []).map((s) => (
                    <tr key={s.id}>
                      <td>{s.equipment_name}</td>
                      <td>{s.problem_reported}</td>
                      <td>{s.service_provider || '—'}</td>
                      <td>{s.technician_name || '—'}</td>
                      <td>
                        {s.out_date ? new Date(s.out_date).toLocaleDateString() : '—'}
                        {' / '}
                        {s.in_date ? new Date(s.in_date).toLocaleDateString() : '—'}
                      </td>
                      <td>{s.service_charge != null ? `₹${fmtMoney(s.service_charge)}` : '—'}</td>
                      <td>{s.status}</td>
                    </tr>
                  ))}
                  {(data?.serviceHistory || []).length === 0 && (
                    <tr><td colSpan={7} style={{ opacity: 0.7 }}>No service records in range</td></tr>
                  )}
                </tbody>
              </Table>
            </ResponsiveDetailTable>
          </TabPanel>

          <TabPanel value={2} sx={{ px: 0 }}>
            <ResponsiveDetailTable
              mobile={
                (data?.amcHistory || []).length === 0 ? (
                  <Typography level="body-sm" sx={{ opacity: 0.7, textAlign: 'center', py: 2 }}>
                    No AMC events in range
                  </Typography>
                ) : (
                  (data?.amcHistory || []).map((h) => (
                    <DetailFieldCard
                      key={h.id}
                      title={h.equipment_name}
                      subtitle={h.event_date ? new Date(h.event_date).toLocaleString() : '—'}
                      fields={[
                        { label: 'Provider', value: h.provider_name || '—' },
                        { label: 'Event', value: h.event_type },
                        { label: 'Details', value: h.details || '—' },
                        { label: 'By', value: h.created_by_name || '—' }
                      ]}
                    />
                  ))
                )
              }
            >
              <Table size="sm" stripe="odd" sx={{ '& thead th': { backgroundColor: 'primary.500', color: 'white' } }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Equipment</th>
                    <th>Provider</th>
                    <th>Event</th>
                    <th>Details</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.amcHistory || []).map((h) => (
                    <tr key={h.id}>
                      <td>{h.event_date ? new Date(h.event_date).toLocaleString() : '—'}</td>
                      <td>{h.equipment_name}</td>
                      <td>{h.provider_name || '—'}</td>
                      <td>{h.event_type}</td>
                      <td>{h.details || '—'}</td>
                      <td>{h.created_by_name || '—'}</td>
                    </tr>
                  ))}
                  {(data?.amcHistory || []).length === 0 && (
                    <tr><td colSpan={6} style={{ opacity: 0.7 }}>No AMC events in range</td></tr>
                  )}
                </tbody>
              </Table>
            </ResponsiveDetailTable>
          </TabPanel>
        </Tabs>
      </Card>
    </Box>
  );
};

export default EquipmentReports;
